import type { ReactNode } from 'react';
import { format } from 'date-fns';
import {
  BookMarked,
  Boxes,
  Briefcase,
  Building2,
  History,
  type LucideIcon,
  Package,
  Sparkles,
  Tags,
  Truck,
  Users,
  Wallet,
} from 'lucide-react';

import type {
  DirEntity,
  DirListParams,
  DirCompany,
  DirContact,
  DirDeal,
  DirLead,
  DirProduct,
  DirRow,
  Multifield,
} from '@/entities/directory';

import { C } from '../ui/refTheme';

/* ── Контекст рендера ячеек (резолверы словарей и пользователей) ── */
export interface DirCtx {
  resolve: (group: string, id?: string | null) => string;
  userName: (id?: number) => string;
  /** Название воронки по её номеру из Bitrix (`category_id` сделки). */
  funnelName: (categoryId?: string) => string;
}

export interface DirColumn {
  key: string;
  label: string;
  width: string;
  align?: 'left' | 'right' | 'center';
  /** Серверное поле сортировки. Есть → заголовок кликабелен. */
  sortKey?: string;
  render: (row: DirRow, ctx: DirCtx) => ReactNode;
}

export type FilterDef =
  | {
      kind: 'select';
      param: keyof DirListParams;
      label: string;
      source: { type: 'dict'; group: string } | { type: 'sections' };
    }
  | {
      kind: 'segment';
      param: keyof DirListParams;
      options: { label: string; value: string | boolean | undefined }[];
    }
  | { kind: 'archive' };

export interface DirConfig {
  slug: DirEntity;
  entity: DirEntity;
  title: string;
  Icon: LucideIcon;
  searchPlaceholder: string;
  columns: DirColumn[];
  filters: FilterDef[];
  /** Статические группы словарей для предзагрузки (resolve + select-фильтры). */
  dicts: string[];
  needsUsers?: boolean;
  needsSections?: boolean;
  /** Динамические группы словарей от текущих строк (напр. стадии по воронке). */
  dynamicDicts?: (rows: unknown[]) => string[];
}

/* ── Хелперы форматирования ── */
const CUR: Record<string, string> = { KZT: '₸', USD: '$', EUR: '€', RUB: '₽' };
const cur = (c?: string) => (c ? (CUR[c] ?? c) : '₸');

const mf = (list?: Multifield[]) => list?.find((m) => m.value)?.value ?? '';
const fio = (r: { name?: string; last_name?: string; second_name?: string }) =>
  [r.last_name, r.name, r.second_name].filter(Boolean).join(' ');

const money = (n?: number, currency?: string) =>
  n == null
    ? ''
    : `${new Intl.NumberFormat('ru-RU').format(Math.round(n))} ${cur(currency)}`;
const num = (n?: number) =>
  n == null ? '' : new Intl.NumberFormat('ru-RU').format(n);
const dt = (s?: string) => {
  if (!s) {
    return '';
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'dd.MM.yyyy');
};

const dash = <span className="dir-muted">—</span>;
const txt = (v?: string | number, cls = '') =>
  v === '' || v == null ? dash : <span className={cls}>{v}</span>;

const CHIP_BG = [
  'rgba(178,106,22,0.25)',
  'rgba(87,183,146,0.30)',
  'rgba(120,175,255,0.35)',
  'rgba(30,42,74,0.12)',
  'rgba(196,82,92,0.22)',
];
const chipColor = (seed = '') => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return CHIP_BG[h % CHIP_BG.length];
};

const statusDot = (label: string, color: string) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      fontSize: 12,
      fontWeight: 600,
      color,
    }}
  >
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }}
    />
    {label}
  </span>
);

/** Статус жизненного цикла (архив/блок/черновик/активен). */
const lifecycle = (r: {
  status?: string;
  blocked?: boolean;
  archived_at?: string;
}) => {
  if (r.archived_at || r.status === 'archived') {
    return statusDot('Архив', 'rgba(30,42,74,0.5)');
  }
  if (r.blocked) {
    return statusDot('Заблокирован', C.red);
  }
  if (r.status === 'draft') {
    return statusDot('Черновик', C.amber);
  }
  return statusDot('Активен', C.green);
};

const nameCell = (name: string, color: string, archived?: boolean) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      minWidth: 0,
      padding: 4,
      borderRadius: 6,
    }}
  >
    <span
      style={{
        width: 22,
        height: 22,
        borderRadius: 6,
        background: color,
        flexShrink: 0,
      }}
    />
    <span className="dir-name">{name || '—'}</span>
    {archived && <span className="dir-archive-pill">Архив</span>}
  </div>
);

const catPill = (text?: string) =>
  text ? <span className="dir-catchip">{text}</span> : dash;

// Приведение типа строки для рендера (таблица дженерик по DirRow).
const as = <T,>(row: DirRow) => row as unknown as T;

/**
 * Группа справочника со стадиями сделки для её воронки.
 *
 * У воронок с номером группа называется `DEAL_STAGE_{номер}`, а у общей воронки
 * (`category_id` = "0" или пусто) — просто `DEAL_STAGE`: группы `DEAL_STAGE_0`
 * на бэке нет. Без этой развилки стадия не резолвилась бы у каждой девятой
 * сделки — общая воронка это 263 записи из 2403.
 */
const dealStageGroup = (categoryId?: string) =>
  !categoryId || categoryId === '0' ? 'DEAL_STAGE' : `DEAL_STAGE_${categoryId}`;

/* ── Конфиги справочников ── */

const contacts: DirConfig = {
  slug: 'contacts',
  entity: 'contacts',
  title: 'Контакты',
  Icon: Users,
  searchPlaceholder: 'Поиск по имени, телефону или email...',
  dicts: ['CONTACT_TYPE'],
  filters: [{ kind: 'archive' }],
  columns: [
    {
      key: 'name',
      label: 'ФИО',
      width: '24%',
      sortKey: 'last_name',
      render: (row) => {
        const r = as<DirContact>(row);
        return nameCell(fio(r) || '—', chipColor(r.id), !!r.archived_at);
      },
    },
    {
      key: 'post',
      label: 'Должность',
      width: '15%',
      render: (row) => txt(as<DirContact>(row).post),
    },
    {
      key: 'phone',
      label: 'Телефон',
      width: '14%',
      render: (row) =>
        txt(mf(as<DirContact>(row).phones), 'dir-muted dir-tabnum dir-sm'),
    },
    {
      key: 'email',
      label: 'Email',
      width: '17%',
      render: (row) => txt(mf(as<DirContact>(row).emails), 'dir-muted'),
    },
    {
      key: 'type',
      label: 'Тип',
      width: '12%',
      render: (row, ctx) =>
        txt(ctx.resolve('CONTACT_TYPE', as<DirContact>(row).type_id)),
    },
    {
      key: 'status',
      label: 'Статус',
      width: '10%',
      align: 'center',
      render: (row) => lifecycle(as<DirContact>(row)),
    },
    {
      key: 'updated',
      label: 'Обновлён',
      width: '10%',
      align: 'right',
      sortKey: 'updated_at',
      render: (row) =>
        txt(dt(as<DirContact>(row).updated_at), 'dir-muted dir-tabnum dir-sm'),
    },
  ],
};

const companyColumns: DirColumn[] = [
  {
    key: 'title',
    label: 'Название',
    width: '24%',
    sortKey: 'title',
    render: (row) => {
      const r = as<DirCompany>(row);
      return nameCell(r.title || '—', chipColor(r.id), !!r.archived_at);
    },
  },
  {
    key: 'type',
    label: 'Тип',
    width: '12%',
    render: (row, ctx) =>
      txt(ctx.resolve('COMPANY_TYPE', as<DirCompany>(row).company_type)),
  },
  {
    key: 'bin',
    label: 'БИН',
    width: '12%',
    render: (row) =>
      txt(as<DirCompany>(row).bin, 'dir-muted dir-tabnum dir-sm'),
  },
  {
    key: 'industry',
    label: 'Отрасль',
    width: '15%',
    render: (row, ctx) =>
      txt(ctx.resolve('INDUSTRY', as<DirCompany>(row).industry)),
  },
  {
    key: 'phone',
    label: 'Телефон',
    width: '13%',
    render: (row) =>
      txt(mf(as<DirCompany>(row).phones), 'dir-muted dir-tabnum dir-sm'),
  },
  {
    key: 'email',
    label: 'Email',
    width: '14%',
    render: (row) => txt(mf(as<DirCompany>(row).emails), 'dir-muted'),
  },
  {
    key: 'status',
    label: 'Статус',
    width: '10%',
    align: 'center',
    render: (row) => lifecycle(as<DirCompany>(row)),
  },
];

const companies: DirConfig = {
  slug: 'companies',
  entity: 'companies',
  title: 'Компании',
  Icon: Building2,
  searchPlaceholder: 'Поиск по названию или БИН...',
  dicts: ['COMPANY_TYPE', 'INDUSTRY'],
  filters: [
    {
      kind: 'select',
      param: 'type',
      label: 'Тип',
      source: { type: 'dict', group: 'COMPANY_TYPE' },
    },
    { kind: 'archive' },
  ],
  columns: companyColumns,
};

const suppliers: DirConfig = {
  slug: 'suppliers',
  entity: 'suppliers',
  title: 'Поставщики',
  Icon: Truck,
  searchPlaceholder: 'Поиск по названию или БИН...',
  dicts: ['COMPANY_TYPE', 'INDUSTRY'],
  filters: [{ kind: 'archive' }],
  columns: companyColumns,
};

const deals: DirConfig = {
  slug: 'deals',
  entity: 'deals',
  title: 'Сделки',
  Icon: Briefcase,
  searchPlaceholder: 'Поиск по названию сделки...',
  dicts: ['DEAL_CATEGORY'],
  needsUsers: true,
  dynamicDicts: (rows) =>
    (rows as DirDeal[]).map((r) => dealStageGroup(r.category_id)),
  filters: [
    {
      kind: 'select',
      param: 'category',
      label: 'Воронка',
      source: { type: 'dict', group: 'DEAL_CATEGORY' },
    },
  ],
  columns: [
    {
      key: 'title',
      label: 'Название',
      width: '25%',
      sortKey: 'title',
      render: (row) => {
        const r = as<DirDeal>(row);
        return nameCell(r.title || '—', chipColor(r.id), false);
      },
    },
    {
      key: 'opportunity',
      label: 'Сумма',
      width: '13%',
      align: 'right',
      render: (row) => {
        const r = as<DirDeal>(row);
        return txt(money(r.opportunity, r.currency_id), 'dir-tabnum');
      },
    },
    {
      key: 'category',
      label: 'Воронка',
      width: '14%',
      /* Имя воронки берём из /v1/funnels: там есть все девять, включая общую
       * (`category_id` = "0"), которой нет в справочнике DEAL_CATEGORY — а
       * резолвер словаря на промахе вернул бы сырое «0» в пилюле. */
      render: (row, ctx) => {
        const r = as<DirDeal>(row);
        return catPill(
          ctx.funnelName(r.category_id) ||
            ctx.resolve('DEAL_CATEGORY', r.category_id)
        );
      },
    },
    {
      key: 'stage',
      label: 'Стадия',
      width: '14%',
      render: (row, ctx) => {
        const r = as<DirDeal>(row);
        return txt(ctx.resolve(dealStageGroup(r.category_id), r.stage_id));
      },
    },
    {
      key: 'status',
      label: 'Статус',
      width: '11%',
      align: 'center',
      render: (row) => {
        const r = as<DirDeal>(row);
        if (r.closed) {
          return statusDot('Закрыта', 'rgba(30,42,74,0.5)');
        }
        if (r.opened === false) {
          return statusDot('Не в работе', C.amber);
        }
        return statusDot('В работе', C.green);
      },
    },
    {
      key: 'begin',
      label: 'Дата',
      width: '11%',
      align: 'right',
      render: (row) =>
        txt(dt(as<DirDeal>(row).begin_date), 'dir-muted dir-tabnum dir-sm'),
    },
    {
      key: 'assigned',
      label: 'Ответственный',
      width: '12%',
      render: (row, ctx) => txt(ctx.userName(as<DirDeal>(row).assigned_by_id)),
    },
  ],
};

const leads: DirConfig = {
  slug: 'leads',
  entity: 'leads',
  title: 'Лиды',
  Icon: Sparkles,
  searchPlaceholder: 'Поиск по названию, имени или телефону...',
  dicts: ['STATUS', 'SOURCE'],
  needsUsers: true,
  filters: [
    {
      kind: 'select',
      param: 'status',
      label: 'Статус',
      source: { type: 'dict', group: 'STATUS' },
    },
    {
      kind: 'select',
      param: 'source',
      label: 'Источник',
      source: { type: 'dict', group: 'SOURCE' },
    },
  ],
  columns: [
    {
      key: 'title',
      label: 'Название',
      width: '22%',
      sortKey: 'title',
      render: (row) => {
        const r = as<DirLead>(row);
        return nameCell(r.title || '—', chipColor(r.id), false);
      },
    },
    {
      key: 'contact',
      label: 'Контакт',
      width: '16%',
      render: (row) => txt(fio(as<DirLead>(row))),
    },
    {
      key: 'company',
      label: 'Компания',
      width: '16%',
      render: (row) => txt(as<DirLead>(row).company_title),
    },
    {
      key: 'status',
      label: 'Статус',
      width: '12%',
      render: (row, ctx) =>
        catPill(ctx.resolve('STATUS', as<DirLead>(row).status_id)),
    },
    {
      key: 'source',
      label: 'Источник',
      width: '12%',
      render: (row, ctx) =>
        txt(ctx.resolve('SOURCE', as<DirLead>(row).source_id)),
    },
    {
      key: 'opportunity',
      label: 'Сумма',
      width: '12%',
      align: 'right',
      render: (row) => {
        const r = as<DirLead>(row);
        return txt(money(r.opportunity, r.currency_id), 'dir-tabnum');
      },
    },
    {
      key: 'assigned',
      label: 'Ответственный',
      width: '10%',
      render: (row, ctx) => txt(ctx.userName(as<DirLead>(row).assigned_by_id)),
    },
  ],
};

const products: DirConfig = {
  slug: 'products',
  entity: 'products',
  title: 'Товары',
  Icon: Package,
  searchPlaceholder: 'Поиск по названию или артикулу...',
  dicts: [],
  needsSections: true,
  filters: [
    {
      kind: 'segment',
      param: 'active',
      options: [
        { label: 'Все', value: undefined },
        { label: 'Активные', value: true },
        { label: 'Архивные', value: false },
      ],
    },
    {
      kind: 'select',
      param: 'section',
      label: 'Раздел',
      source: { type: 'sections' },
    },
    { kind: 'archive' },
  ],
  columns: [
    {
      key: 'name',
      label: 'Наименование',
      width: '26%',
      sortKey: 'name',
      render: (row) => {
        const r = as<DirProduct>(row);
        return nameCell(
          r.name || '—',
          chipColor(r.section_name || r.id),
          r.active === false || !!r.archived_at
        );
      },
    },
    {
      key: 'code',
      label: 'Артикул',
      width: '12%',
      render: (row) =>
        txt(as<DirProduct>(row).code, 'dir-muted dir-tabnum dir-sm'),
    },
    {
      key: 'section',
      label: 'Раздел',
      width: '16%',
      render: (row) => catPill(as<DirProduct>(row).section_name),
    },
    {
      key: 'price',
      label: 'Закуп. цена',
      width: '13%',
      align: 'right',
      render: (row) => {
        const r = as<DirProduct>(row);
        return txt(
          money(r.purchasing_price, r.purchasing_currency),
          'dir-tabnum'
        );
      },
    },
    {
      key: 'quantity',
      label: 'Кол-во',
      width: '11%',
      align: 'center',
      render: (row) => txt(num(as<DirProduct>(row).quantity), 'dir-tabnum'),
    },
    {
      key: 'measure',
      label: 'Ед.',
      width: '9%',
      render: (row) => txt(as<DirProduct>(row).measure_name, 'dir-muted'),
    },
    {
      key: 'status',
      label: 'Статус',
      width: '13%',
      align: 'center',
      render: (row) => {
        const r = as<DirProduct>(row);
        if (r.archived_at) {
          return statusDot('Архив', 'rgba(30,42,74,0.5)');
        }
        return r.active === false
          ? statusDot('Неактивен', C.amber)
          : statusDot('Активен', C.green);
      },
    },
  ],
};

export const DIR_REGISTRY: Record<DirEntity, DirConfig> = {
  contacts,
  companies,
  suppliers,
  deals,
  leads,
  products,
};

/** Порядок в поповере навбара. */
export const DIR_ORDER: DirEntity[] = [
  'contacts',
  'companies',
  'suppliers',
  'deals',
  'leads',
  'products',
];

export function isDirEntity(v?: string): v is DirEntity {
  return !!v && v in DIR_REGISTRY;
}

/** Доп-экраны в поповере (после справочников-сущностей). */
export const EXTRA_NAV: { slug: string; title: string; Icon: LucideIcon }[] = [
  { slug: 'dictionaries', title: 'Словари', Icon: BookMarked },
  { slug: 'price-lists', title: 'Прайс-листы', Icon: Tags },
  { slug: 'catalog', title: 'Каталог', Icon: Boxes },
  { slug: 'payments', title: 'Платежи', Icon: Wallet },
  { slug: 'audit', title: 'Журнал аудита', Icon: History },
];
